import React, { useState } from 'react';
import type { TableColumnsType } from 'antd';
import { Table, Button, Modal, InputNumber, message } from 'antd';
import { useMutation } from '@apollo/client';
import { VirtualAccountType } from '../graphql/types/VirtualAccountType';
import { UPDATE_VIRTUAL_ACCOUNT_OVERDRAFT_LIMIT, UpdateVirtualAccountOverdraftLimitResponse, UpdateVirtualAccountOverdraftLimitVariables } from '../graphql/mutations/updateVirtualAccountOverdraftLimit';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

interface VirtualAccountRowProps {
    rowData: VirtualAccountType[];
    loading?: boolean;
    onRefresh?: () => void;
}

export const VirtualAccountColumns: React.FC<VirtualAccountRowProps> = ({
    rowData,
    loading,
    onRefresh
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<VirtualAccountType | null>(null);
    const [overdraftLimitValue, setOverdraftLimitValue] = useState<number | null>(null);

    const [updateOverdraftLimit, { loading: updateLoading }] = useMutation<
        UpdateVirtualAccountOverdraftLimitResponse,
        UpdateVirtualAccountOverdraftLimitVariables
    >(UPDATE_VIRTUAL_ACCOUNT_OVERDRAFT_LIMIT);

    const handleSetOverdraftLimit = (record: VirtualAccountType) => {
        setSelectedAccount(record);
        // 将字符串格式的透支额度转换为数字（后端返回的是元，需要显示元）
        const currentLimit = parseFloat(record.overdraftLimit || '0');
        setOverdraftLimitValue(currentLimit);
        setIsModalOpen(true);
    };

    const handleModalOk = async () => {
        if (!selectedAccount || overdraftLimitValue === null) {
            message.error('请输入透支额度');
            return;
        }

        try {
            // 将元转换为分（后端存储单位为分）
            const overdraftLimitInCents = Math.round(overdraftLimitValue * 100);
            
            const result = await updateOverdraftLimit({
                variables: {
                    virtualAccountId: parseInt(selectedAccount.virtualAccountId),
                    overdraftLimit: overdraftLimitInCents
                }
            });

            if (result.data?.updateVirtualAccountOverdraftLimit) {
                message.success('透支额度设置成功');
                setIsModalOpen(false);
                setSelectedAccount(null);
                setOverdraftLimitValue(null);
                onRefresh?.();
            } else {
                message.error('透支额度设置失败');
            }
        } catch (error) {
            console.error('设置透支额度失败:', error);
            message.error('透支额度设置失败');
        }
    };

    const handleModalCancel = () => {
        setIsModalOpen(false);
        setSelectedAccount(null);
        setOverdraftLimitValue(null);
    };

    const virtualAccountColumns: TableColumnsType<VirtualAccountType> = [
        {
            title: '客户名称',
            dataIndex: 'clientName',
            key: 'clientName',
            align: 'center',
        },
        {
            title: '客户ID',
            dataIndex: 'clientId',
            key: 'clientId',
            align: 'center',
        },
        {
            title: '账户ID',
            dataIndex: 'virtualAccountId',
            key: 'virtualAccountId',
            align: 'center',
        },
        {
            title: '账户类型',
            dataIndex: 'type',
            key: 'type',
            align: 'center',
        },
        {
            title: '余额',
            key: 'balance',
            align: 'center',
            render: (_, record) => {
                return record.balance ? `$${record.balance}` : '$0.00';
            },
        },
        {
            title: '透支额度',
            key: 'overdraftLimit',
            align: 'center',
            render: (_, record) => {
                return record.overdraftLimit ? `$${record.overdraftLimit}` : '$0.00';
            },
        },
        {
            title: '币种',
            dataIndex: 'currency',
            key: 'currency',
            align: 'center',
            render: (currency) => {
                const currencyMap: Record<string, string> = {
                    'USD': '美元',
                    'CNY': '人民币'
                };
                return currencyMap[currency] || currency;
            },
        },
        {
            title: '开户时间',
            dataIndex: 'activeTime',
            key: 'activeTime',
            align: 'center',
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_BALANCE]}>
                    <Button
                        type="link"
                        onClick={() => handleSetOverdraftLimit(record)}
                    >
                        设置透支额度
                    </Button>
                </HasResource>
            ),
        }
    ];

    return (
        <>
            <Table
                rowKey={(record) => record.virtualAccountId}
                columns={virtualAccountColumns}
                dataSource={rowData}
                scroll={{ x: 1950 }}
                size="middle"
                className="mb-4"
                pagination={false}
                loading={loading}
            />

            <Modal
                title="设置透支额度"
                open={isModalOpen}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                confirmLoading={updateLoading}
                okText="确定"
                cancelText="取消"
            >
                <div className="py-4">
                    <div className="mb-4">
                        <span className="text-gray-600">账户ID: </span>
                        <span className="font-medium">{selectedAccount?.virtualAccountId}</span>
                    </div>
                    <div className="mb-4">
                        <span className="text-gray-600">客户名称: </span>
                        <span className="font-medium">{selectedAccount?.clientName || '-'}</span>
                    </div>
                    <div className="mb-4">
                        <span className="text-gray-600">当前余额: </span>
                        <span className="font-medium">${selectedAccount?.balance || '0.00'}</span>
                    </div>
                    <div>
                        <label className="block text-gray-600 mb-2">透支额度 ($):</label>
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                            value={overdraftLimitValue}
                            onChange={(value) => setOverdraftLimitValue(value)}
                            placeholder="请输入透支额度"
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};
